import { useState } from 'react';

import { Button, DateStreak, ValueChart } from '@opositandopn/ui';
import {
  IoCalendarOutline,
  IoCaretForwardCircleOutline,
  IoCheckmarkCircleOutline,
  IoRemoveCircleOutline,
} from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

import ActivityImg from '../../../assets/images/d99997c8-e7be-4f13-acbf-f956058937c5.webp';
import { BackdropDialog } from '../../../components/shared/backdrop/BackdropDialog';
import { MetricStrip } from '../../../components/shared/metric-strip/MetricStrip';
import { SectionCard } from '../../../components/shared/section-card/SectionCard';
import { THEORY_SUBJECT_ID } from '../../../constants/subjects';
import { useProfile } from '../../../context/ProfileContext';
import { useAppActions } from '../../../state/appStore';
import { useCreateSelfMultipleChoiceAssessmentAttempt } from '../../assessments/api/useMultipleChoiceAssessments';
import { MultipleChoiceAssessmentAttempt } from '../../assessments/components/multiple-choice-assessment-attempt/MultipleChoiceAssessmentAttempt';
import { useGetStreak, useRedeemProtectors } from '../../users/api/useUsers';

import styles from './ActivityCard.module.scss';

function ActivityCard() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { setShowGetPremiumPopup } = useAppActions();

  const [showAttempt, setShowAttempt] = useState(false);

  const subjectId = THEORY_SUBJECT_ID;

  const { data: metrics, isLoading } = useGetStreak({
    userId: profile?.id ?? '',
  });

  // Derive activeDates and disabledDates from Days array
  const activeDates =
    metrics?.days
      ?.filter((day) => day.status === 'active')
      .map((day) => day.date) ?? [];
  const protectedDates =
    metrics?.days
      ?.filter((day) => day.status === 'protected')
      .map((day) => day.date) ?? [];
  const disabledDates =
    metrics?.days
      ?.filter((day) => day.status === 'disabled')
      .map((day) => day.date) ?? [];

  const { mutateAsync: createSelfMultipleChoiceAssessmentAttempt } =
    useCreateSelfMultipleChoiceAssessmentAttempt();

  const { mutate: redeemProtectors, isPending: isRedeemingProtectors } =
    useRedeemProtectors();

  const handleCreateAttempt = () => {
    if (!profile.subscription?.isActive) {
      setShowGetPremiumPopup(true);
      return;
    }

    setShowAttempt(true);
  };

  return (
    <>
      <SectionCard
        loading={isLoading}
        title="Reto personal"
        image={ActivityImg}
        tooltip="Cada día tendrás un nuevo reto de 10 preguntas de distintos temas, combinando preguntas falladas y nuevas."
        button={
          metrics?.isActiveToday ? (
            <Button
              variant="outline-primary"
              disabled
              suffixIcon={<IoCheckmarkCircleOutline />}
            >
              Reto completado
            </Button>
          ) : metrics?.isDisabledToday ? (
            <Button
              variant="outline-primary"
              disabled
              suffixIcon={<IoRemoveCircleOutline />}
            >
              Reto no disponible
            </Button>
          ) : (
            <Button
              variant="outline-primary"
              suffixIcon={<IoCaretForwardCircleOutline />}
              onClick={handleCreateAttempt}
            >
              Empezar reto
            </Button>
          )
        }
        icon={<IoCalendarOutline />}
        className={styles['section-card-content']}
      >
        <MetricStrip
          variant="panel"
          metrics={[
            { label: 'Días de racha', value: metrics?.currentDays ?? 0 },
            { label: 'Mejor racha', value: metrics?.bestDays ?? 0 },
            { label: 'Días activos', value: metrics?.totalActiveDays ?? 0 },
          ].map(({ label, value }) => ({
            label,
            value: (
              <ValueChart
                value={value}
                size="md"
                format={{ maximumFractionDigits: 2 }}
              />
            ),
          }))}
        />
        <DateStreak
          today={new Date()}
          highlightedDates={activeDates}
          skippedDates={protectedDates}
          disabledDates={disabledDates}
        >
          <div className={styles['date-streak-content']}>
            <div>
              {metrics?.isDisabledToday ? (
                <>Tómate el día de hoy para respirar, seguimos mañana 💙</>
              ) : (
                <>
                  {(() => {
                    const currentDays = metrics?.currentDays ?? 0;
                    if (currentDays === 0) {
                      return (
                        <>
                          Empieza a sumar días. Cada pequeño paso{' '}
                          <strong>cuenta</strong> 💙
                        </>
                      );
                    }
                    if (currentDays === 1) {
                      return (
                        <>
                          Primer día <strong>conseguido</strong>. ¡Esto acaba de
                          empezar! 🚀
                        </>
                      );
                    }
                    if (currentDays < 7) {
                      return (
                        <>
                          Ya son <strong>{currentDays} días</strong> de racha.
                          Vamos a por la semana 🔥
                        </>
                      );
                    }
                    if (currentDays < 30) {
                      return (
                        <>
                          <strong>{currentDays} días</strong> seguidos. Se nota
                          el progreso ⚡
                        </>
                      );
                    }
                    if (currentDays < 100) {
                      return (
                        <>
                          <strong>{currentDays} días</strong> de constancia. Muy
                          buen ritmo 🚀
                        </>
                      );
                    }
                    return (
                      <>
                        <strong>{currentDays} días</strong> de racha. Todo un
                        referente 👑
                      </>
                    );
                  })()}
                </>
              )}
            </div>
            <div className={styles['footer']}>
              <DateStreak.Protectors
                count={metrics?.protectors ?? 0}
                onRedeem={() => {
                  if (profile?.id) {
                    redeemProtectors(
                      { userId: profile.id, amount: 1 },
                      {
                        onError: (error) => {
                          console.error('Failed to redeem protectors:', error);
                        },
                      },
                    );
                  }
                }}
                loading={isLoading || isRedeemingProtectors}
                disabled={
                  profile?.credits !== undefined && profile.credits < 25
                }
              />
              <DateStreak.Calendar />
            </div>
          </div>
        </DateStreak>
      </SectionCard>
      <BackdropDialog open={showAttempt}>
        <MultipleChoiceAssessmentAttempt
          // The daily streak is answered on screen — there is no paper sitting to
          // choose, so it starts without asking.
          mode="interactive"
          onStart={(mode) =>
            createSelfMultipleChoiceAssessmentAttempt({ subjectId, mode })
          }
          onFinish={(attempt) => {
            setShowAttempt(false);
            navigate(
              `/materia/${subjectId}/simulacros/mc/resultados/${attempt.id}`,
            );
          }}
          onClose={() => setShowAttempt(false)}
        />
      </BackdropDialog>
    </>
  );
}

export { ActivityCard };
